import { lazy, Suspense, useMemo } from "react";
import Box from "@mui/material/Box";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import ModalCreateWithIA from "../Modal/ModalCreateWithIA";
import CarouselDisplay from "./CarouselDisplay";
import MediaUrlItem from "./MediaUrlItem";
import ContentRecommendationsBanner from "./ContentRecommendationsBanner";
import {
  ANALYZING_CONTENT,
  CONTENT_RECOMMENDATIONS_ALL_COMPLETED,
  CONTENT_RECOMMENDATIONS_BANNER_TITLE,
  NO_CONTENT_RECOMMENDATIONS,
} from "../../i18n/keysTranslations";
import { useInternalMediaUrls } from "../../hooks/useInternalMediaUrls";
import { useContentRecommendations } from "../../hooks/useContentRecommendations";
import { useTranslationApp } from "../../lib/i18next";
import {
  getContentRecommendationKey,
  isCarouselItem,
} from "../../utils/mediaUrlsAnalysis";

// Same clip used by BatchImageGrid so only assistive tech reads this node.
const STATUS_ANNOUNCEMENT_SX = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const ModalContentRecommendations = lazy(
  () => import("../Modal/ModalContentRecommendations")
);

const MediaUrlsAnalysisContainer = ({
  mediaUrls,
  onRemoveItem: onRemoveItemCallback,
  objective,
  platform,
  onChangeItem = () => {},
  campaignContext,
  disableAnalysis = false,
  itemSize = 160,
  suggestedCreativesCount = null,
  onCallbackCampaignMediaImport,
  containerRef,
}) => {
  const { t } = useTranslationApp();
  const { validMediaUrls, onChangeData, loaderProgress, setLoaderProgress } =
    useInternalMediaUrls(mediaUrls);

  const {
    contentRecommendations,
    completedContentRecommendationKeys,
    isNoContentRecommendations,
    hasAtLeastOneCreativeNotYoutube,
    isRecommendationsModalOpen,
    setIsRecommendationsModalOpen,
    createWithIAState,
    onSelectContentRecommendation,
    onCloseCreateWithIAModal,
    buildWrappedCampaignMediaImport,
    showAnalyzingContentLoader,
    showContentRecommendationsSummary,
  } = useContentRecommendations({
    mediaUrls,
    platform,
    objective,
    campaignContext,
    disableAnalysis,
    validMediaUrls,
  });

  const onWrappedCallbackCampaignMediaImport = useMemo(
    () => buildWrappedCampaignMediaImport(onCallbackCampaignMediaImport),
    [buildWrappedCampaignMediaImport, onCallbackCampaignMediaImport]
  );

  const onRemoveItem = (urlOrCarouselItem) => {
    if (
      typeof urlOrCarouselItem === "object" &&
      urlOrCarouselItem?.isCarousel
    ) {
      const originalIndex = mediaUrls.findIndex(
        (item) =>
          item?.isCarousel === true &&
          item?.carouselImages &&
          Array.isArray(item.carouselImages)
      );
      if (originalIndex !== -1) {
        onRemoveItemCallback(originalIndex);
      }
      return;
    }

    const originalIndex = mediaUrls.findIndex(
      (item) => item?.url === urlOrCarouselItem
    );
    if (originalIndex !== -1) {
      onRemoveItemCallback(originalIndex);
    }
  };

  let contentRecommendationsStatus = null;
  if (showAnalyzingContentLoader) {
    contentRecommendationsStatus = "loading";
  } else if (showContentRecommendationsSummary) {
    contentRecommendationsStatus = "suggestions";
  } else if (isNoContentRecommendations && !disableAnalysis) {
    contentRecommendationsStatus = "empty";
  }

  const completedRecommendationKeys = new Set(
    completedContentRecommendationKeys
  );
  const allRecommendationsCompleted =
    contentRecommendations.length > 0 &&
    contentRecommendations.every((recommendation) =>
      completedRecommendationKeys.has(
        getContentRecommendationKey(recommendation)
      )
    );

  let contentRecommendationsStatusAnnouncement = "";
  switch (contentRecommendationsStatus) {
    case "loading":
      contentRecommendationsStatusAnnouncement = t(ANALYZING_CONTENT);
      break;
    case "suggestions":
      contentRecommendationsStatusAnnouncement = t(
        allRecommendationsCompleted
          ? CONTENT_RECOMMENDATIONS_ALL_COMPLETED
          : CONTENT_RECOMMENDATIONS_BANNER_TITLE
      );
      break;
    case "empty":
      contentRecommendationsStatusAnnouncement = t(NO_CONTENT_RECOMMENDATIONS);
      break;
    default:
      contentRecommendationsStatusAnnouncement = "";
  }

  if (!hasAtLeastOneCreativeNotYoutube) {
    return null;
  }

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            width: "100%",
          }}
        >
          {validMediaUrls.length > 0 &&
            validMediaUrls.map((mediaUrlData, index) => {
              if (isCarouselItem(mediaUrlData)) {
                const carouselUrls = mediaUrlData.carouselImages.map((img) =>
                  typeof img === "string" ? img : img?.url || img
                );
                const carouselMediaData = mediaUrlData.carouselImages.map(
                  (img) => (typeof img === "object" ? img.mediaData || {} : {})
                );

                return (
                  <CarouselDisplay
                    key={`carousel_${index}_${carouselUrls[0] || index}`}
                    urls={carouselUrls}
                    mediaData={carouselMediaData}
                    onRemove={() => onRemoveItem(mediaUrlData)}
                    itemSize={itemSize}
                  />
                );
              }

              return (
                <MediaUrlItem
                  key={mediaUrlData.url || `item_${index}`}
                  mediaUrlData={mediaUrlData}
                  onRemove={() => onRemoveItem(mediaUrlData?.url)}
                  onChangeData={onChangeData}
                  onAnalysisFinished={(analysis) => {
                    onChangeItem({
                      url: mediaUrlData.url,
                      data: { analysis },
                    });
                  }}
                  disableAnalysis={disableAnalysis}
                  itemSize={itemSize}
                />
              );
            })}
        </Box>

        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={STATUS_ANNOUNCEMENT_SX}
        >
          {contentRecommendationsStatusAnnouncement}
        </Box>

        {contentRecommendationsStatus ? (
          <ContentRecommendationsBanner
            status={contentRecommendationsStatus}
            contentRecommendations={contentRecommendations}
            completedContentRecommendationKeys={
              completedContentRecommendationKeys
            }
            validMediaUrlsCount={validMediaUrls.length}
            suggestedCreativesCount={suggestedCreativesCount}
            onSeeMore={() => setIsRecommendationsModalOpen(true)}
            onSelectRecommendation={onSelectContentRecommendation}
          >
            {contentRecommendationsStatus === "loading" ? (
              <LinearLoaderWithTime
                time={30000}
                sx={{
                  mt: 0,
                  width: "100%",
                }}
                sxLabel={{
                  display: "none",
                }}
                progress={loaderProgress}
                autoIncrement={true}
                onProgressChange={setLoaderProgress}
              />
            ) : null}
          </ContentRecommendationsBanner>
        ) : null}
      </Box>

      {isRecommendationsModalOpen && (
        <Suspense fallback={null}>
          <ModalContentRecommendations
            modalOpen={isRecommendationsModalOpen}
            onCloseModal={() => setIsRecommendationsModalOpen(false)}
            contentRecommendations={contentRecommendations}
            completedContentRecommendationKeys={
              completedContentRecommendationKeys
            }
            onSelectRecommendation={onSelectContentRecommendation}
          />
        </Suspense>
      )}

      {createWithIAState && (
        <ModalCreateWithIA
          key={createWithIAState.key}
          modalOpen={true}
          onCloseModal={onCloseCreateWithIAModal}
          initialValues={createWithIAState.initialValues}
          doReUpdateInitialValues={true}
          onCallbackCampaignMediaImport={onWrappedCallbackCampaignMediaImport}
        />
      )}
    </>
  );
};

export default MediaUrlsAnalysisContainer;
